<template>
  <Teleport to="body">
    <section
      v-if="notice && !modalOpen"
      ref="noticeElement"
      class="billing-notice"
      :class="{
        'billing-notice--blocked': notice.state === 'blocked',
        'billing-notice--admin': notice.adminExempt,
      }"
      role="status"
      aria-live="polite"
    >
      <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3 2.8 19h18.4L12 3Zm0 6v4m0 3h.01" /></svg>
      <p>
        <strong>{{ notice.state === 'blocked' ? 'Tu suscripción necesita atención.' : `Tienes un pago pendiente hace ${notice.overdueDays} días.` }}</strong>
        <span>{{ money(notice.amount) }} pendiente · {{ notice.plan }}</span>
      </p>
      <button type="button" :disabled="paying" @click="payNow">
        {{ paying ? 'Preparando pago…' : (notice.canPayNow ? 'Pagar ahora' : 'Actualizar pago') }}
      </button>
    </section>

    <div
      v-if="modalOpen && notice"
      class="billing-lock"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="billing-lock-title"
      aria-describedby="billing-lock-description"
      @click.self="focusPayment"
    >
      <article class="billing-lock__card">
        <div class="billing-lock__icon" aria-hidden="true">
          <svg viewBox="0 0 24 24"><rect x="5" y="10" width="14" height="10" rx="2" /><path d="M8 10V7a4 4 0 0 1 8 0v3" /></svg>
        </div>
        <h2 id="billing-lock-title">Regulariza tu suscripción para continuar</h2>
        <p id="billing-lock-description" class="billing-lock__intro">
          Debes pagar las mensualidades pendientes para recuperar el acceso a ItSave. El desbloqueo será automático cuando Mercado Pago confirme el pago.
        </p>

        <dl class="billing-lock__summary">
          <div><dt>Plan</dt><dd>{{ notice.plan }}</dd></div>
          <div><dt>Deuda desde</dt><dd>{{ dateLabel(notice.dueSince) }}</dd></div>
          <div><dt>Mensualidades</dt><dd>{{ notice.periodCount }}</dd></div>
          <div class="billing-lock__total"><dt>Total por pagar</dt><dd>{{ money(notice.amount) }}</dd></div>
        </dl>

        <p v-if="paymentError" class="billing-lock__error" role="alert">{{ paymentError }}</p>
        <button ref="paymentButton" type="button" class="billing-lock__pay" :disabled="paying" @click="payNow">
          <span v-if="paying" class="billing-lock__spinner" aria-hidden="true"></span>
          <svg v-else viewBox="0 0 24 24" aria-hidden="true"><path d="M3 7h18v10H3zM3 10h18M7 14h3" /></svg>
          {{ paying ? 'Preparando pago seguro…' : (notice.canPayNow ? 'Pagar ahora con Mercado Pago' : 'Actualizar método de pago') }}
        </button>
        <p class="billing-lock__secure">Pago seguro procesado por Mercado Pago</p>
      </article>
    </div>
  </Teleport>
</template>

<script setup>
import axios from 'axios'
import { nextTick, onBeforeUnmount, onMounted, ref } from 'vue'

const emit = defineEmits(['blocking-change', 'notice-offset'])
const notice = ref(null)
const modalOpen = ref(false)
const paying = ref(false)
const paymentError = ref('')
const paymentButton = ref(null)
const noticeElement = ref(null)
let companyId = ''
let modalTimer = null
let companyTimer = null
let refreshTimer = null
let requestInFlight = false
let noticeResizeObserver = null
let disposed = false
let authenticationRequired = false
let consecutiveFailures = 0
let nextRefreshAt = 0

const STATUS_REFRESH_MS = 120000
const BLOCKED_REFRESH_MS = 30000
const MAX_RETRY_MS = 300000

const money = (value) => new Intl.NumberFormat('es-CL', {
  style: 'currency',
  currency: notice.value?.currency || 'CLP',
  maximumFractionDigits: 0,
}).format(Number(value || 0))

const dateLabel = (value) => {
  if (!value) return '—'
  return new Intl.DateTimeFormat('es-CL', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(`${value}T12:00:00Z`))
}

function clearModalTimer() {
  if (modalTimer) window.clearTimeout(modalTimer)
  modalTimer = null
}

function syncNoticeOffset() {
  const height = noticeElement.value && !modalOpen.value
    ? Math.ceil(noticeElement.value.getBoundingClientRect().height)
    : 0
  emit('notice-offset', height)
}

function refreshNoticeObserver() {
  nextTick(() => {
    noticeResizeObserver?.disconnect()
    if (noticeElement.value) noticeResizeObserver?.observe(noticeElement.value)
    syncNoticeOffset()
  })
}

function setModalOpen(value) {
  modalOpen.value = value
  emit('blocking-change', value)
  document.body.style.overflow = value ? 'hidden' : ''
  if (value) nextTick(focusPayment)
  refreshNoticeObserver()
}

function scheduleBlockingModal() {
  if (modalOpen.value || modalTimer) return
  modalTimer = window.setTimeout(() => {
    modalTimer = null
    if (notice.value?.state === 'blocked') setModalOpen(true)
  }, 10000)
}

function resetGuard() {
  clearModalTimer()
  notice.value = null
  paymentError.value = ''
  setModalOpen(false)
}

function scheduleRefresh(delay = notice.value?.state === 'blocked' ? BLOCKED_REFRESH_MS : STATUS_REFRESH_MS) {
  if (refreshTimer) window.clearTimeout(refreshTimer)
  refreshTimer = null
  if (disposed || authenticationRequired) return
  nextRefreshAt = Date.now() + delay
  refreshTimer = window.setTimeout(loadStatus, delay)
}

function refreshWhenVisible() {
  if (!document.hidden && Date.now() >= nextRefreshAt) loadStatus()
}

async function loadStatus() {
  if (disposed || authenticationRequired || requestInFlight) return
  if (document.hidden) {
    scheduleRefresh()
    return
  }
  const selectedCompany = localStorage.getItem('empresa_id') || ''
  if (!selectedCompany) {
    resetGuard()
    scheduleRefresh()
    return
  }
  requestInFlight = true
  let retryDelay
  try {
    const response = await axios.get('/api/subscription/access-status', {
      params: { empresa_id: selectedCompany },
      timeout: 15000,
    })
    if (disposed || selectedCompany !== localStorage.getItem('empresa_id')) return
    consecutiveFailures = 0
    const result = response.data || {}
    if (!result.status || result.state === 'ok') {
      resetGuard()
      return
    }
    notice.value = result
    paymentError.value = ''
    if (result.state === 'blocked' && !result.adminExempt) scheduleBlockingModal()
    else {
      clearModalTimer()
      setModalOpen(false)
    }
    refreshNoticeObserver()
  } catch (error) {
    if (disposed || selectedCompany !== localStorage.getItem('empresa_id')) return
    if ([401, 403].includes(error?.response?.status)) {
      if (error.response.status === 401) authenticationRequired = true
      consecutiveFailures = 0
      resetGuard()
    } else {
      consecutiveFailures = Math.min(consecutiveFailures + 1, 5)
      retryDelay = Math.min(BLOCKED_REFRESH_MS * 2 ** (consecutiveFailures - 1), MAX_RETRY_MS)
    }
  } finally {
    requestInFlight = false
    if (!disposed && selectedCompany !== (localStorage.getItem('empresa_id') || '')) {
      loadStatus()
    } else {
      scheduleRefresh(retryDelay)
    }
  }
}

async function payNow() {
  if (!notice.value || paying.value) return
  paymentError.value = ''
  if (!notice.value.canPayNow) {
    window.location.assign(notice.value.billingUrl || '/facturacion_suscripcion')
    return
  }

  paying.value = true
  try {
    const formData = new FormData()
    formData.append('empresa_id', companyId || localStorage.getItem('empresa_id') || '')
    formData.append('subscription_id', notice.value.subscriptionId)
    const response = await axios.post('/crear_pago_deuda_mercadopago', formData)
    if (!response.data?.init_point) throw new Error('Mercado Pago no entregó el enlace de pago.')
    window.location.assign(response.data.init_point)
  } catch (error) {
    paymentError.value = error?.response?.data?.error || error.message || 'No pudimos preparar el pago. Inténtalo nuevamente.'
    focusPayment()
  } finally {
    paying.value = false
  }
}

function focusPayment() {
  nextTick(() => paymentButton.value?.focus())
}

function preventEscape(event) {
  if (modalOpen.value && event.key === 'Escape') {
    event.preventDefault()
    focusPayment()
  }
}

onMounted(() => {
  if (typeof ResizeObserver !== 'undefined') {
    noticeResizeObserver = new ResizeObserver(syncNoticeOffset)
  }
  companyId = localStorage.getItem('empresa_id') || ''
  loadStatus()
  companyTimer = window.setInterval(() => {
    const nextCompanyId = localStorage.getItem('empresa_id') || ''
    if (nextCompanyId === companyId) return
    companyId = nextCompanyId
    resetGuard()
    loadStatus()
  }, 1500)
  document.addEventListener('visibilitychange', refreshWhenVisible)
  document.addEventListener('keydown', preventEscape, true)
})

onBeforeUnmount(() => {
  disposed = true
  clearModalTimer()
  noticeResizeObserver?.disconnect()
  if (companyTimer) window.clearInterval(companyTimer)
  if (refreshTimer) window.clearTimeout(refreshTimer)
  document.removeEventListener('visibilitychange', refreshWhenVisible)
  document.removeEventListener('keydown', preventEscape, true)
  document.body.style.overflow = ''
  emit('blocking-change', false)
  emit('notice-offset', 0)
})
</script>

<style scoped>
.billing-notice{position:fixed;z-index:800;top:64px;left:var(--app-sidebar-width,76px);right:0;min-height:46px;display:flex;align-items:center;gap:11px;padding:7px 22px;background:#fff8e9;border-bottom:1px solid #efd59e;color:#27314d;box-shadow:0 4px 14px rgba(31,42,68,.08);font-family:Poppins,system-ui,sans-serif;transition:left .3s cubic-bezier(.4,0,.2,1)}.billing-notice--admin{z-index:40}.billing-notice--blocked{background:#fff1ef;border-color:#efbbb4}.billing-notice>svg{width:20px;height:20px;flex:0 0 auto;fill:none;stroke:#af6900;stroke-width:2;stroke-linecap:round;stroke-linejoin:round}.billing-notice--blocked>svg{stroke:#c13b32}.billing-notice p{display:flex;align-items:baseline;gap:8px;flex:1;min-width:0;margin:0;font-size:12.5px;line-height:1.35}.billing-notice strong{font-weight:600}.billing-notice span{color:#68738e;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}.billing-notice button{height:32px;flex:0 0 auto;padding:0 14px;border:0;border-radius:8px;background:#7047eb;color:#fff;font:600 12px Poppins,system-ui,sans-serif;cursor:pointer}.billing-notice button:hover{background:#6138db}.billing-notice button:focus-visible{outline:3px solid rgba(112,71,235,.28);outline-offset:2px}.billing-notice button:disabled{opacity:.65;cursor:wait}
.billing-lock{position:fixed;z-index:10050;inset:0;display:flex;align-items:center;justify-content:center;padding:20px;background:rgba(18,27,52,.62);font-family:Poppins,system-ui,sans-serif}.billing-lock__card{width:min(456px,100%);max-height:calc(100vh - 40px);overflow:auto;padding:28px;border-radius:18px;background:#fff;color:#202a45;box-shadow:0 28px 80px rgba(10,18,42,.32);animation:lock-in .24s cubic-bezier(.2,.8,.2,1)}.billing-lock__icon{width:50px;height:50px;display:flex;align-items:center;justify-content:center;margin-bottom:17px;border-radius:14px;background:#eee9ff;color:#6942e7}.billing-lock__icon svg{width:24px;height:24px;fill:none;stroke:currentColor;stroke-width:2;stroke-linecap:round;stroke-linejoin:round}.billing-lock h2{margin:0;font-size:21px;line-height:1.3;font-weight:700;letter-spacing:-.25px}.billing-lock__intro{margin:8px 0 0;color:#606b85;font-size:13px;line-height:1.6}.billing-lock__summary{margin:19px 0;border:1px solid #e5e8f1;border-radius:13px;padding:5px 15px}.billing-lock__summary div{display:flex;justify-content:space-between;gap:18px;padding:9px 0;font-size:13px}.billing-lock__summary div+div{border-top:1px solid #eef0f5}.billing-lock__summary dt{color:#7c879e}.billing-lock__summary dd{margin:0;text-align:right;font-weight:600}.billing-lock__summary .billing-lock__total{align-items:center}.billing-lock__total dt{color:#202a45;font-weight:600}.billing-lock__total dd{font-size:19px}.billing-lock__pay{width:100%;height:49px;display:flex;align-items:center;justify-content:center;gap:9px;border:0;border-radius:11px;background:#7047eb;color:#fff;font:600 14px Poppins,system-ui,sans-serif;cursor:pointer;box-shadow:0 8px 20px rgba(112,71,235,.25)}.billing-lock__pay:hover{background:#6138db}.billing-lock__pay:focus-visible{outline:3px solid rgba(112,71,235,.3);outline-offset:3px}.billing-lock__pay:disabled{opacity:.72;cursor:wait}.billing-lock__pay svg{width:18px;height:18px;fill:none;stroke:currentColor;stroke-width:2;stroke-linecap:round;stroke-linejoin:round}.billing-lock__secure{margin:9px 0 0;text-align:center;color:#8993a8;font-size:11.5px}.billing-lock__error{margin:0 0 12px;padding:9px 11px;border-radius:9px;background:#fff0ee;color:#ad322b;font-size:12px;line-height:1.45}.billing-lock__spinner{width:16px;height:16px;border:2px solid rgba(255,255,255,.45);border-top-color:#fff;border-radius:50%;animation:spin .7s linear infinite}
@keyframes lock-in{from{opacity:0;transform:translateY(12px) scale(.985)}}@keyframes spin{to{transform:rotate(360deg)}}
@media(max-width:767px){.billing-notice{top:64px;left:0;padding:8px 12px;gap:8px}.billing-notice p{display:block}.billing-notice p span{display:none}.billing-notice button{padding:0 10px}.billing-lock{align-items:flex-end;padding:10px}.billing-lock__card{padding:23px 20px 19px;border-radius:18px 18px 14px 14px}.billing-lock h2{font-size:19px}}
@media(prefers-reduced-motion:reduce){.billing-notice{transition:none}.billing-lock__card{animation:none}}
</style>
